import { Button, Flex, InformationCircleIcon, Panel, Spinner, Text } from 'components'
import { useWorkspace } from 'components/providers'
import { useEffect, useState } from 'react'
import { IWorkspaceIntegration, PrimaryLeadProperties, primaryLeadProperties } from 'types'
import { getCRMLeadFields } from 'utils'
import { useIntegrationForConfiguration } from '../../utils'
import PropertyMapper from '../PropertyMapper'

export default function IntegrationConfiguration() {
  const { updateWorkspace, workspace } = useWorkspace()
  const integration = useIntegrationForConfiguration()
  const [configLoading, setConfigLoading] = useState(false)
  const [integrationLeadFields, setIntegrationLeadFields] = useState<Record<string, string>>()
  const [leadMapping, setLeadMapping] = useState<PrimaryLeadProperties>()
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (integration?.status === 'needs-mapping' && !integrationLeadFields) handleConfigureMapping()
  }, [integration])

  async function handleConfigureMapping() {
    setDismissed(false)
    if (!integration?.instanceUrl || !integration.accessToken) return
    try {
      setConfigLoading(true)
      const response = await getCRMLeadFields({
        workspaceId: workspace?.id!,
        integrationTool: integration.tool.name,
      })

      const crmFields = Object.values(response.data).reduce(
        (acc: Record<string, string>, field: any) => {
          acc[field.name] = field.type
          return acc
        },
        {}
      )
      setIntegrationLeadFields(crmFields)
      setConfigLoading(false)
    } catch (error) {}
  }

  async function handleSaveMapping() {
    if (!workspace || !integration) return
    const updatedIntegrations =
      (workspace?.workspaceIntegrations?.map(i =>
        i.tool === integration.tool ? { ...i, mapping: leadMapping, status: 'connected' } : i
      ) as IWorkspaceIntegration[]) || []
    await updateWorkspace({ workspaceIntegrations: updatedIntegrations })
    resetMapping()
  }

  function resetMapping() {
    setLeadMapping(undefined)
    setIntegrationLeadFields(undefined)
  }

  if (!integration || integration.status !== 'needs-mapping') return null

  return (
    <Panel direction="column" gap={1} p={4}>
      {dismissed ? (
        <Flex gap={3} justify="center" direction="column">
          <Text textStyle="h5">{integration.tool.name} Configuration</Text>
          <Flex gap={2} color="red" opacity={0.5} align="center">
            <InformationCircleIcon size="md" type="outline" />
            <Text textStyle="bodyXSmall">
              Configure mapping to complete the integration set up between Momentify and{' '}
              {integration.tool.name}
            </Text>
          </Flex>
        </Flex>
      ) : (
        <>
          <Flex justify="space-between">
            <Text textStyle="h5">{integration.tool.name} Configuration</Text>
            <Flex gap={2}>
              <Button
                variant="outline"
                onClick={() => {
                  setDismissed(true)
                  resetMapping()
                }}
              >
                Dismiss
              </Button>
              <Button palette="primary" onClick={handleSaveMapping}>
                Save Mapping
              </Button>
            </Flex>
          </Flex>
          {configLoading && <Spinner />}
          {integrationLeadFields && (
            <Flex direction="column" gap={3}>
              <PropertyMapper
                inputFieldLabel={`${integration.tool.name} Fields`}
                valueFieldLabel="Field Type"
                inputMap={integrationLeadFields}
                onChange={setLeadMapping}
                properties={primaryLeadProperties}
                py={2}
              />
            </Flex>
          )}
        </>
      )}
    </Panel>
  )
}
